// Scenario analysis — what-if projections from today's real figures:
// registered and not-registered ID holders (per county, subcounty or tribe)
// and the current registration pace. Only the slider values are assumptions.

import React, { useMemo, useState } from 'react';
import { Box, Grid, LinearProgress, Slider, Typography } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { KenyaMap } from '@components/KenyaMap';
import { areaName, monthLabel, useDataScope } from './data';
import { emptyNrb, nrbLabel, normalizeNrb, useIdRows, useIdTrend, useTribeSummary } from './agg';
import { C, Column, DataTable, fmt, fmtShort, Kpi, Panel, Section, Toggle } from './ui';

const ALL = { preset: 'all', from: '', to: '' };

interface ScenarioRow {
  key: string;
  name: string;
  ids: number;
  registered: number;
  notRegistered: number;
  coverage: number;
  newRegs: number;
  projected: number;
  projectedCoverage: number;
  votes: number;
  gap: number;
}

const SliderField: React.FC<{
  label: string; value: number; onChange: (v: number) => void; min: number; max: number; help: string; color: string;
}> = ({ label, value, onChange, min, max, help, color }) => (
  <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography sx={{ fontSize: '0.62rem', color: C.muted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color }}>{value}%</Typography>
    </Box>
    <Slider value={value} min={min} max={max} step={1} onChange={(_, v) => onChange(v as number)} sx={{ color, py: 1 }} />
    <Typography sx={{ fontSize: '0.65rem', color: C.muted, mt: -0.5 }}>{help}</Typography>
  </Box>
);

export const ScenarioSection: React.FC<{ county: string }> = ({ county }) => {
  const [groupBy, setGroupBy] = useState<'area' | 'tribe'>('area');
  const [uptake, setUptake] = useState(30);
  const [turnout, setTurnout] = useState(65);
  const [target, setTarget] = useState(85);
  const scope = useDataScope();
  const f = normalizeNrb({ ...emptyNrb, county });
  const areas = useIdRows(f, ALL);
  const byCounty = useIdRows(emptyNrb, ALL);
  const tribes = useTribeSummary(f);
  const trend = useIdTrend(f, ALL, 'month');
  const scopeName = areaName(county) || 'Kenya';
  const levelName = county ? nrbLabel('subcounty', county) : 'County';

  const project = (key: string, name: string, registered: number, notRegistered: number): ScenarioRow => {
    const ids = registered + notRegistered;
    const newRegs = Math.round((notRegistered * uptake) / 100);
    const projected = registered + newRegs;
    const needed = Math.max(0, Math.ceil((ids * target) / 100) - registered);
    return {
      key, name, ids, registered, notRegistered,
      coverage: ids ? (registered / ids) * 100 : 0,
      newRegs, projected,
      projectedCoverage: ids ? (projected / ids) * 100 : 0,
      votes: Math.round((projected * turnout) / 100),
      gap: needed,
    };
  };

  const rows: ScenarioRow[] = useMemo(() => (groupBy === 'tribe'
    ? tribes.rows.map((t) => project(t.tribe, t.tribe, t.registered, t.notRegistered))
    : areas.rows.map((r) => project(r.key, areaName(r.name), r.registered, r.notRegistered))
  ), [groupBy, tribes.rows, areas.rows, uptake, turnout, target]); // eslint-disable-line react-hooks/exhaustive-deps

  const tot = rows.reduce((a, r) => ({
    ids: a.ids + r.ids, registered: a.registered + r.registered, newRegs: a.newRegs + r.newRegs,
    projected: a.projected + r.projected, votes: a.votes + r.votes, gap: a.gap + r.gap,
  }), { ids: 0, registered: 0, newRegs: 0, projected: 0, votes: 0, gap: 0 });
  const covNow = tot.ids ? (tot.registered / tot.ids) * 100 : 0;
  const covProj = tot.ids ? (tot.projected / tot.ids) * 100 : 0;
  const neededForTarget = Math.max(0, Math.ceil((tot.ids * target) / 100) - tot.registered);

  // Current pace: the average of the last three complete months of registrations.
  const pace = useMemo(() => {
    const last = scope.last;
    const pts = trend.points.filter((p) => !last || p.period < last.slice(0, 7) || last.slice(8, 10) >= '28');
    const recent = pts.slice(-3);
    const perMonth = recent.length ? recent.reduce((s, p) => s + p.registered, 0) / recent.length : 0;
    return { perMonth, months: recent.map((p) => monthLabel(p.period)) };
  }, [trend.points, scope.last]);
  const monthsToTarget = pace.perMonth > 0 ? neededForTarget / pace.perMonth : Infinity;

  const unit = groupBy === 'tribe' ? 'Tribe' : levelName;
  const columns: Column<ScenarioRow>[] = [
    { key: 'name', label: unit, value: (r) => r.name },
    { key: 'ids', label: 'ID holders', value: (r) => r.ids, kind: 'num', color: C.blue },
    { key: 'registered', label: 'Registered now', value: (r) => r.registered, kind: 'num', color: C.green },
    { key: 'coverage', label: 'Coverage now', value: (r) => r.coverage, kind: 'percent', total: () => covNow },
    { key: 'newRegs', label: 'New registrations', value: (r) => r.newRegs, kind: 'num', color: C.purple },
    { key: 'projected', label: 'Projected registered', value: (r) => r.projected, kind: 'num', color: C.green },
    { key: 'projectedCoverage', label: 'Projected coverage', value: (r) => r.projectedCoverage, kind: 'pct', total: () => covProj },
    { key: 'votes', label: 'Projected votes cast', value: (r) => r.votes, kind: 'num', color: C.yellow },
    { key: 'gap', label: `Still needed for ${target}%`, value: (r) => Math.max(0, Math.ceil((r.ids * target) / 100) - r.projected), kind: 'num', color: C.orange },
  ];

  const chartRows = [...rows].sort((a, b) => b.ids - a.ids).slice(0, 15)
    .map((r) => ({ name: r.name, Registered: r.registered, 'New registrations': r.newRegs }));
  const loading = groupBy === 'tribe' ? tribes.fetching : areas.fetching;

  return (
    <Section id="scenario" icon={<InsightsIcon />} title="Scenario analysis" accent={C.purple}
      subtitle={`${scopeName} · what-if projections from today's registered and not-registered ID holders`}>
      <Panel sx={{ height: 'auto', mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <SliderField label="Unregistered ID holders who register" value={uptake} onChange={setUptake} min={0} max={100} color={C.purple}
            help={`${fmt(tot.ids - tot.registered)} ID holders are not registered today`} />
          <SliderField label="Turnout" value={turnout} onChange={setTurnout} min={30} max={100} color={C.yellow}
            help="Share of registered voters who vote" />
          <SliderField label="Target coverage" value={target} onChange={setTarget} min={50} max={100} color={C.orange}
            help={`Coverage today: ${covNow.toFixed(1)}%`} />
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: C.muted, fontWeight: 700, letterSpacing: 0.5, mb: 0.75 }}>PROJECT BY</Typography>
            <Toggle value={groupBy} onChange={(v) => setGroupBy(v as 'area' | 'tribe')}
              options={[{ value: 'area', label: levelName }, { value: 'tribe', label: 'Tribe' }]} />
          </Box>
        </Box>
      </Panel>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Kpi label="Projected registered" value={tot.projected} color={C.green} sub={`+${fmt(tot.newRegs)} on today's ${fmt(tot.registered)}`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Projected coverage" value={`${covProj.toFixed(1)}%`} color={C.purple} sub={`from ${covNow.toFixed(1)}% today`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Projected votes cast" value={tot.votes} color={C.yellow} sub={`at ${turnout}% turnout`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label={`Time to ${target}% at current pace`} color={C.orange}
          value={neededForTarget === 0 ? 'Reached' : Number.isFinite(monthsToTarget) ? `${monthsToTarget < 24 ? monthsToTarget.toFixed(1) + ' months' : (monthsToTarget / 12).toFixed(1) + ' years'}` : '—'}
          sub={pace.perMonth ? `${fmt(neededForTarget)} more needed · ${fmt(pace.perMonth)} a month (${pace.months.join(', ')})` : 'no recent registrations'} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {groupBy === 'area' && !county && (
          <Grid item xs={12} md={4}>
            <Panel title="Projected coverage map" sx={{ height: 440 }}>
              <Box sx={{ height: 370, p: 1 }}>
                <KenyaMap background={C.cardAlt} labelColor={C.muted} countyData={byCounty.rows.map((r) => {
                  const p = project(r.key, r.name, r.registered, r.notRegistered);
                  return { name: r.name, lat: 0, lng: 0, registered: p.projected, unregistered: p.ids - p.projected, percentage: p.projectedCoverage };
                })} />
              </Box>
            </Panel>
          </Grid>
        )}
        <Grid item xs={12} md={groupBy === 'area' && !county ? 8 : 12}>
          <Panel title={`Registered now and new registrations · top ${chartRows.length} by ID holders`} sx={{ height: 440 }}>
            {loading && <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.purple } }} />}
            <Box sx={{ height: 380, p: 1 }}>
              <ResponsiveContainer>
                <BarChart data={chartRows} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barCategoryGap={3}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtShort} tick={{ fill: C.muted, fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fill: C.text, fontSize: 11 }} interval={0} />
                  <Tooltip contentStyle={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                    cursor={{ fill: C.faint }} formatter={(v: number, n: string) => [v.toLocaleString(), n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Registered" stackId="a" fill={C.green} stroke={C.card} strokeWidth={2} />
                  <Bar dataKey="New registrations" stackId="a" fill={C.purple} stroke={C.card} strokeWidth={2} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Panel>
        </Grid>
      </Grid>

      <Panel title={`Projection by ${unit.toLowerCase()} · ${scopeName}`} sx={{ height: 480 }}>
        {loading && <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.purple } }} />}
        <DataTable rows={rows} columns={columns} rowKey={(r) => r.key} maxHeight={390} fileName={`scenario-${groupBy}-${scopeName}`}
          searchPlaceholder={`Search ${unit.toLowerCase()}…`} emptyText={loading ? 'Loading…' : 'No rows'} />
      </Panel>

      <Typography sx={{ fontSize: '0.68rem', color: C.muted, mt: 1.5 }}>
        Starting figures are today's real numbers (IEBC roll and NRB records{groupBy === 'tribe' ? '; tribe from NRB records' : ''}). New registrations = unregistered ID holders × the share you set; votes cast = projected registered × turnout; the pace is the average of the last three complete months of registrations. Projections, not results.
      </Typography>
    </Section>
  );
};
